import { PostsAdsApi } from "../posts/posts-ads-api";
import { AdTypes } from "../../constants/ad-types";
import { CommonUtils } from "../../util/common-utils";
import { productPlaceholderAdsForm } from "./product-placeholder-ads-form";

type UnknownRecord = Record<string, unknown>;

type PlaceholderRoot = ParentNode | Document | HTMLElement | null | undefined;

export class ProductPlaceholderAdsSetup {
    public root: ParentNode;

    public constructor(root: ParentNode) {
        this.root = root;
    }

    public static init(rootEl?: PlaceholderRoot): void {
        const root = rootEl instanceof Document || rootEl instanceof HTMLElement ? rootEl : document;
        void new ProductPlaceholderAdsSetup(root).applyAds();
    }

    public async applyAds(): Promise<void> {
        const cards = this.getPendingCards();
        if (!cards.length) return;

        const adType = this.resolveAdType(cards[0]?.dataset.productPlaceholderAdType);
        if (!adType) return;

        const ads = await PostsAdsApi.fetchAds(adType, Math.max(10, cards.length * 2));
        if (!ads.length) return;

        cards.forEach((card, index) => {
            const ad = ads[index % ads.length];
            if (!ad) return;
            this.applyAd(card, ad, adType);
        });
    }

    private getPendingCards(): HTMLElement[] {
        return productPlaceholderAdsForm.getPendingCards(this.root);
    }

    private resolveAdType(raw: string | undefined): string | null {
        const candidate = String(raw || "")
            .trim()
            .toUpperCase();
        if (!candidate) return null;
        return Object.values(AdTypes).includes(candidate) ? candidate : null;
    }

    private applyAd(card: HTMLElement, ad: UnknownRecord, adType: string): void {
        if (card.dataset.productPlaceholderAdApplied === "1") return;
        if (
            String(ad.ad_type || "")
                .trim()
                .toUpperCase() !== adType
        )
            return;

        const cardParts = productPlaceholderAdsForm.getCardParts(card);
        if (!cardParts) return;
        const { header, titleRow, metaLine, bodyLine, footerLine, footerLeft, footerRight } = cardParts;

        const adTitle = this.resolveText(ad.ad_title, "Sponsored slot");
        const adBody = this.resolveText(ad.ad_body, "Featured products and sponsored picks will appear here soon.");
        const linkUrl = this.resolveLinkUrl(ad);
        const footerLabel = this.resolveFooterLabel(linkUrl);
        const fallbackImageUrl = this.resolveFallbackImageUrl(card);
        const imageUrl = this.resolveImageUrl(ad, fallbackImageUrl);
        const hasImage = imageUrl !== fallbackImageUrl || String(ad.image_path || "").trim() !== "";

        this.renderHeader(card, header, adTitle, imageUrl, fallbackImageUrl, linkUrl, hasImage);
        this.renderTitle(titleRow, adTitle, linkUrl);
        this.renderLine(metaLine, "bi-award", "icon icon--secondary", "Sponsored");
        this.renderLine(bodyLine, "bi-megaphone", "icon icon--primary", adBody);
        this.renderFooterLine(footerLine, footerLabel);
        this.renderFooterLeft(footerLeft, "Sponsored");
        this.renderFooterRight(footerRight, !!linkUrl);

        card.classList.remove("product-card--placeholder");
        card.classList.toggle("product-card--no-image", !hasImage);
        card.dataset.productPlaceholderAdApplied = "1";
        card.dataset.adId = String(ad.ad_id || "").trim();
        card.dataset.productPlaceholderAdType = adType;
        card.dataset.adType = adType;
        card.setAttribute("aria-disabled", linkUrl ? "false" : "true");
    }

    private resolveText(value: unknown, fallback: string): string {
        const text = String(value || "").trim();
        return text || fallback;
    }

    private resolveLinkUrl(ad: UnknownRecord): string {
        const raw = String(ad.link_url || ad.url_link || "").trim();
        return raw;
    }

    private resolveFooterLabel(linkUrl: string): string {
        if (!linkUrl) return "AD";

        try {
            const parsed = new URL(linkUrl, window.location.origin);
            const host = parsed.hostname.replace(/^www\./, "").trim();
            return host || "AD";
        } catch {
            return "AD";
        }
    }

    private resolveFallbackImageUrl(card: HTMLElement): string {
        const image = productPlaceholderAdsForm.getFallbackImage(card);
        return String(image?.getAttribute("src") || "").trim();
    }

    private resolveImageUrl(ad: UnknownRecord, fallbackImageUrl: string): string {
        const rawPath = String(ad.image_path || "").trim();
        if (!rawPath) return fallbackImageUrl;

        return CommonUtils.resolveUrl(rawPath);
    }

    private renderHeader(
        card: HTMLElement,
        header: HTMLElement,
        adTitle: string,
        imageUrl: string,
        fallbackImageUrl: string,
        linkUrl: string,
        hasImage: boolean,
    ): void {
        const badges = productPlaceholderAdsForm.getImageBadges(header)?.cloneNode(true) ?? null;
        const imageRoot = document.createElement("div");
        imageRoot.className = "product-card__image-zoom-root";

        const imageWrapper = document.createElement("div");
        imageWrapper.className = hasImage
            ? "product-card__image-wrapper"
            : "product-card__image-wrapper product-card__image-wrapper--no-image";

        const image = document.createElement("img");
        image.className = "card-img-top";
        image.alt = adTitle;
        image.loading = "lazy";
        image.src = imageUrl || fallbackImageUrl;
        image.onerror = () => {
            image.onerror = null;
            image.src = fallbackImageUrl;
            card.classList.add("product-card--no-image");
            imageWrapper.classList.add("product-card__image-wrapper--no-image");
            return null;
        };

        const adBadge = document.createElement("span");
        adBadge.className = "ad-slot__badge";
        adBadge.setAttribute("aria-label", "Advertisement");
        adBadge.textContent = "AD";

        imageWrapper.append(image);
        imageWrapper.append(adBadge);
        if (badges instanceof HTMLElement) {
            imageWrapper.append(badges);
        }

        imageRoot.append(imageWrapper);

        if (linkUrl) {
            const anchor = document.createElement("a");
            anchor.className = "product-card__header-link";
            anchor.href = linkUrl;
            anchor.target = "_blank";
            anchor.rel = "noopener noreferrer";
            anchor.append(imageRoot);
            header.replaceChildren(anchor);
            return;
        }

        header.replaceChildren(imageRoot);
    }

    private renderTitle(titleRow: HTMLElement, adTitle: string, linkUrl: string): void {
        if (linkUrl) {
            const anchor = document.createElement("a");
            anchor.className = "product-card__title";
            anchor.href = linkUrl;
            anchor.target = "_blank";
            anchor.rel = "noopener noreferrer";
            anchor.textContent = adTitle;
            titleRow.replaceChildren(anchor);
            return;
        }

        const span = document.createElement("span");
        span.className = "product-card__title";
        span.textContent = adTitle;
        titleRow.replaceChildren(span);
    }

    private renderLine(line: HTMLElement, iconName: string, iconClassName: string, text: string): void {
        const icon = document.createElement("i");
        icon.className = `bi ${iconName} ${iconClassName}`;

        const span = document.createElement("span");
        span.textContent = text;

        line.replaceChildren(icon, span);
    }

    private renderFooterLine(line: HTMLElement, text: string): void {
        const span = document.createElement("span");
        span.className = "product-price__content";
        span.textContent = text;
        line.replaceChildren(span);
    }

    private renderFooterLeft(footerLeft: HTMLElement, text: string): void {
        const span = document.createElement("span");
        span.className = "no-reviews-text";
        span.textContent = text;
        footerLeft.replaceChildren(span);
    }

    private renderFooterRight(footerRight: HTMLElement, hasLink: boolean): void {
        const iconWrap = document.createElement("div");
        iconWrap.className = "like-display product-like-display";
        iconWrap.setAttribute("aria-hidden", "true");

        const icon = document.createElement("i");
        icon.className = `bi ${hasLink ? "bi-box-arrow-up-right" : "bi-megaphone"}`;
        iconWrap.append(icon);
        footerRight.replaceChildren(iconWrap);
    }
}
